// utils/createAiPayload.ts

import type { ProductData } from '@/apis/types';

interface AlertTrigger {
  thresholdValue?: string;
  thresholdExplanation?: string;
  currentValue?: number;
  unit?: string;
  timestamp?: string;
  recommendedAction?: string;
}

export interface AlertData {
  id?: string;
  title?: string;
  detail?: string;
  notification_type?: string;
  isCritical?: boolean;
  trigger?: AlertTrigger;
  primary_contact?: Contact;
  recommended_action?: string;
}

interface Location {
  name?: string;
  address?: string;
  notes?: string;
}

interface Contact {
  id?: string;
  name?: string;
  email?: string;
  phone?: string;
  role?: string;
  preferred_language?: string;
  order_index?: number;
  contact_type?: string;
}

interface Product {
  temperature_min?: number;
  temperature_max?: number;
  temperature_unit?: string;
  humidity_min?: number;
  humidity_max?: number;
  humidity_unit?: string;
  pressure_min?: number;
  pressure_max?: number;
  pressure_unit?: string;
  light?: number;
  shock?: number;
  tilt?: number;
}

interface MonitorInfo {
  temperature?: number | string;
  humidity?: number | string;
  pressure?: number | string;
  light?: number | string;
  shock_value?: number | string;
  tilt?: number | string;
  timestamp?: string;
}

interface Sensor {
  type?: string;
  battery?: {
    currentLevel?: number;
    estimatedLife?: number;
    uom?: string;
  };
}

interface Courier {
  title?: string;
}

// TODO(tim): base this off of SingleShipmentData
interface Shipment {
  reference_number?: string;
  current_location?: string;
  from_location?: Location;
  to_location?: Location;
  tp_primary_contact?: Contact;
  products?: ProductData[];
  product?: Product;
  weight_kg?: string | number;
  volume?: string;
  volume_m3?: string | number;
  monitor_info?: MonitorInfo | MonitorInfo[];
  actual_pickup_date?: string;
  scheduled_pickup_date?: string;
  estimated_delivery_date?: string;
  transport_provider_eta?: string;
  scheduled_delivery_date?: string;
  customer_preferred_pickup_date?: string;
  customer_preferred_delivery_date?: string;
  scheduled_pickup_date_formatted?: string;
  scheduled_delivery_date_formatted?: string;
  actual_pickup_date_formatted?: string;
  expected_recipient_name?: string;
  status?: string;
  updated_at?: string;
  courier?: Courier;
  courier_tracking_id?: string;
  return_shipment_courier_tracking_id?: string;
  courier_service_type?: string;
  courier_service_level?: string;
  link_code?: string;
  hawb?: string;
  mawb?: string;
  delivery_status?: string;
  sensor_id?: string;
  sensor?: Sensor;
  schedule_viability_window?: string;
}

interface CreateAiPayloadParams {
  alertData?: AlertData;
  shipment?: Shipment;
  companyName?: string;
  aiCompanyName?: string;
  companyID?: string;
  emailSubject?: string;
  emailMessage?: string;
  isEmailPayload?: boolean;
  userID?: string;
  transferPhone?: string;
  includeSensitiveProducts?: boolean;
}

interface EmailPayload {
  to_email?: string;
  to_name?: string;
  subject?: string;
  body_html?: string;
  body_text?: string;
  from_email: string;
  from_name?: string;
  reply_to: string;
}

interface AiPayload {
  ai_alert_event_uuid?: string;
  company?: string;
  company_id?: string;
  triggered_by?: string;
  language?: string;
  agent_config?: {
    ai_company_name?: string;
  };
  alert: {
    name?: string;
    description?: string;
    severity: { type?: string };
    recommendedAction?: string;
    trigger: {
      thresholdValue: number | string;
      thresholdExplanation?: string;
      currentValue: number | null;
      unit?: string;
      timestamp: string;
    };
  };
  shipment: {
    referenceId?: string;
    currentLocation?: string;
    origin: {
      locationName?: string;
      address?: string;
      timestamp?: string;
      notes: string;
    };
    destination: {
      locationName?: string;
      address?: string;
      eta?: string;
      notes: string;
      recipient_name?: string;
    };
    products?: ProductData[] | { product_type: string }[];
    weight_kg?: string | number;
    dimensions?: string;
    volume?: string | number;
    sensor_readings: Array<{
      temperature: {
        range: { min?: number; max?: number };
        uom?: string;
        latest_reading: { value?: number | string; timestamp?: string };
      };
      light: {
        range: { min: null; max?: number };
        uom: string;
        latest_reading: { value?: number | string; timestamp?: string };
      };
      humidity: {
        range: { min?: number; max?: number };
        uom?: string;
        latest_reading: { value?: number | string; timestamp?: string };
      };
      pressure: {
        range: { min?: number; max?: number };
        uom?: string;
        latest_reading: { value?: number | string; timestamp?: string };
      };
      shock: {
        range: { min: null; max?: number };
        uom: string;
        latest_reading: { value?: number | string; timestamp?: string };
      };
      tilt: {
        range: { min: null; max?: number };
        uom: string;
        latest_reading: { value?: number | string; timestamp?: string };
      };
    }>;
    status: {
      pickupTime?: string;
      preferredPickupTime?: string;
      preferredDeliveryTime?: string;
      scheduledPickupTime?: string;
      scheduledDeliveryTime?: string;
      lastUpdate?: string;
      delivered?: string;
    };
    transport: {
      provider?: string;
      trackingId?: string;
      status?: string;
      returnTrackingId?: string;
      service_type?: string;
      service_level?: string;
      link_code?: string;
      hawb?: string;
      mawb?: string;
    };
    sensor: {
      id?: string;
      type?: string;
      battery: {
        currentLevel?: number;
        estimatedLife?: number;
        uom?: string;
      };
    };
    viabilityWindow: {
      remainingTime?: string;
      endTime: null;
      isCritical?: boolean;
    };
  };
  contacts:
    | {
        primary?: {
          name?: string;
          phone?: string;
          role?: string;
        };
        transfer_phone?: string;
      }
    | Record<string, never>;
  email?: EmailPayload;
}

interface CreateAiStatusPayloadParams {
  alertData?: AlertData;
  shipment?: Shipment;
  ai_status?: string;
  id?: string;
}

interface AiStatusPayload {
  alert_id?: string;
  ai_status?: string;
  primary_contact:
    | {
        id?: string;
        name?: string;
        email?: string;
        role?: string;
        phone?: string;
        language?: string;
        contact_type?: string;
      }
    | Record<string, never>;
}

const filterProductsBySensitivity = (
  products: ProductData[] | undefined,
  includeSensitive: boolean = true
): ProductData[] | { product_type: string }[] | undefined => {
  if (!products) return products;

  if (includeSensitive) {
    return products;
  }

  return products.map((product) => ({
    product_type: product.product_type || 'Unknown',
  }));
};

/**
 * Creates an AI payload for alert processing
 *
 * @param alertData - Alert information and trigger details
 * @param shipment - Shipment data including location, products, and contacts
 * @param companyName - Name of the company
 * @param emailSubject - Subject for email notifications
 * @param emailMessage - Message content for email notifications
 * @param isEmailPayload - Whether to include email payload in response
 * @param userID - ID of the user triggering the alert
 * @param includeSensitiveProducts - Whether to include full product details (default true)
 *                                   Get this value from the decoded JWT token: decodedToken.include_sensitive_products
 * @returns Formatted AI payload object
 */
export const createAiPayload = ({
  alertData,
  shipment,
  companyName,
  aiCompanyName,
  companyID,
  emailSubject,
  emailMessage,
  isEmailPayload = false,
  userID,
  transferPhone,
  includeSensitiveProducts = true,
}: CreateAiPayloadParams): AiPayload => {
  const primaryMonitorInfo = Array.isArray(shipment?.monitor_info)
    ? shipment.monitor_info[0]
    : shipment?.monitor_info;
  const primary = alertData?.primary_contact;

  const payload: AiPayload = {
    ai_alert_event_uuid: alertData?.id,
    company: companyName,
    company_id: companyID,
    triggered_by: userID,
    language: primary?.preferred_language ?? null,
    ...(aiCompanyName && {
      agent_config: {
        ai_company_name: aiCompanyName,
      },
    }),

    alert: {
      name: alertData?.title,
      description: alertData?.detail,
      severity: { type: alertData?.notification_type },
      recommendedAction:
        alertData?.trigger?.recommendedAction || alertData?.recommended_action,
      trigger: {
        thresholdValue: alertData?.trigger?.thresholdValue ?? 0,
        thresholdExplanation: alertData?.trigger?.thresholdExplanation,
        currentValue: alertData?.trigger?.currentValue ?? null,
        unit: alertData?.trigger?.unit,
        timestamp: alertData?.trigger?.timestamp ?? new Date().toISOString(),
      },
    },

    shipment: {
      referenceId: shipment?.reference_number,
      currentLocation: shipment.current_location,
      origin: {
        ...(shipment?.from_location?.name !== 'Unknown Location' && {
          locationName: shipment?.from_location?.name,
        }),
        address: shipment?.from_location?.address,
        timestamp:
          shipment?.actual_pickup_date || shipment?.scheduled_pickup_date,
        notes: '',
      },

      destination: {
        ...(shipment?.to_location?.name !== 'Unknown Location' && {
          locationName: shipment?.to_location?.name,
        }),
        address: shipment?.to_location?.address,
        eta:
          shipment?.estimated_delivery_date ||
          shipment?.transport_provider_eta ||
          shipment?.scheduled_delivery_date,
        notes: shipment?.to_location?.notes ?? '',
        recipient_name: shipment?.expected_recipient_name,
      },
      products: filterProductsBySensitivity(
        shipment?.products,
        includeSensitiveProducts
      ),
      weight_kg: shipment?.weight_kg,
      dimensions: shipment?.volume,
      volume: shipment?.volume_m3,
      sensor_readings: [
        {
          temperature: {
            range: {
              min: shipment?.product?.temperature_min,
              max: shipment?.product?.temperature_max,
            },
            uom: shipment?.product?.temperature_unit,
            latest_reading: {
              value: primaryMonitorInfo?.temperature,
              timestamp: primaryMonitorInfo?.timestamp,
            },
          },
          light: {
            range: { min: null, max: shipment?.product?.light },
            uom: 'lux',
            latest_reading: {
              value: primaryMonitorInfo?.light,
              timestamp: primaryMonitorInfo?.timestamp,
            },
          },
          humidity: {
            range: {
              min: shipment?.product?.humidity_min,
              max: shipment?.product?.humidity_max,
            },
            uom: shipment?.product?.humidity_unit,
            latest_reading: {
              value: primaryMonitorInfo?.humidity,
              timestamp: primaryMonitorInfo?.timestamp,
            },
          },
          pressure: {
            range: {
              min: shipment?.product?.pressure_min,
              max: shipment?.product?.pressure_max,
            },
            uom: shipment?.product?.pressure_unit,
            latest_reading: {
              value: primaryMonitorInfo?.pressure,
              timestamp: primaryMonitorInfo?.timestamp,
            },
          },
          shock: {
            range: { min: null, max: shipment?.product?.shock },
            uom: 'G',
            latest_reading: {
              value: primaryMonitorInfo?.shock_value,
              timestamp: primaryMonitorInfo?.timestamp,
            },
          },
          tilt: {
            range: { min: null, max: shipment?.product?.tilt },
            uom: 'deg',
            latest_reading: {
              value: primaryMonitorInfo?.tilt,
              timestamp: primaryMonitorInfo?.timestamp,
            },
          },
        },
      ],
      status: {
        pickupTime: shipment?.actual_pickup_date_formatted,
        preferredPickupTime: shipment?.customer_preferred_pickup_date,
        preferredDeliveryTime: shipment?.customer_preferred_delivery_date,
        scheduledPickupTime: shipment?.scheduled_pickup_date_formatted,
        scheduledDeliveryTime: shipment?.scheduled_delivery_date_formatted,
        lastUpdate: shipment?.updated_at,
        delivered: shipment?.status,
      },
      transport: {
        provider: shipment?.courier?.title,
        trackingId: shipment?.courier_tracking_id,
        status: shipment?.delivery_status,
        returnTrackingId: shipment?.return_shipment_courier_tracking_id,
        service_type: shipment?.courier_service_type,
        service_level: shipment?.courier_service_level,
        link_code: shipment?.link_code,
        hawb: shipment?.hawb,
        mawb: shipment?.mawb,
      },
      sensor: {
        id: shipment?.sensor_id,
        type: shipment?.sensor?.type,
        battery: {
          currentLevel: shipment?.sensor?.battery?.currentLevel,
          estimatedLife: shipment?.sensor?.battery?.estimatedLife,
          uom: shipment?.sensor?.battery?.uom,
        },
      },
      viabilityWindow: {
        remainingTime: shipment?.schedule_viability_window,
        endTime: null,
        isCritical: alertData?.isCritical,
      },
    },

    contacts: primary
      ? {
          primary: {
            name: primary?.name,
            phone: primary?.phone,
            role: primary?.role,
          },
          ...(transferPhone && { transfer_phone: transferPhone }),
        }
      : transferPhone
        ? { transfer_phone: transferPhone }
        : {},
  };
  // Optionally add email block if data is present
  if (isEmailPayload === true) {
    payload.email = {
      to_email: primary?.email,
      to_name: primary?.name,
      subject: emailSubject,
      body_html: emailMessage.replace(/\n/g, '<br/>'),
      body_text: emailMessage ?? '',
      from_email: 'customer@zoomlogi.com',
      from_name: companyName,
      reply_to: 'customer@zoomlogi.com',
    };
  }

  return payload;
};

/**
 * Creates an AI status payload for alert status updates
 *
 * @param shipment - Shipment data containing primary contact information
 * @param ai_status - Current AI processing status
 * @param id - Alert ID
 * @returns Formatted AI status payload object
 */
export const createAiStatusPayload = ({
  alertData,
  ai_status,
  id,
}: CreateAiStatusPayloadParams): AiStatusPayload => {
  const primary = alertData?.primary_contact;
  return {
    alert_id: id,
    ai_status: ai_status,
    primary_contact: primary
      ? {
          id: primary?.id,
          name: primary?.name,
          email: primary?.email,
          role: primary?.role,
          phone: primary?.phone,
          language: primary?.preferred_language,
          contact_type:
            primary?.contact_type || 'TRANSPORT_PROVIDER_PRIMARY_CONTACT',
        }
      : {},
  };
};
